import React from 'react'
import { useNavigate } from 'react-router'
import { Container, Stack, Heading, Text, EmptyCard, Box, SimpleTabs } from '@/components/lib'
import api from '../../../api'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'
import utils from '@/utils/utils'
import FromScratch from './FromScratch'
import TrialBuilder from './TrialBuilder'
import Demo from './Demo'

// The new-trial page offers more than one way to start. Each tab owns its own
// form and API call; once a trial exists they all hand it to `onCreated` so
// the store, the user's permissions and the navigation are dealt with once.
export default function NewTrial () {
  const navigate = useNavigate()
  const { user, updateUser, team, updateTrial } = useStore()
  const hasBuilder = utils.hasFeature(team, 'trialBuilder')
  useTitle('New Trial')

  // `stay` leaves the page where it is (the trial builder shows its report
  // first); the tab then calls `goToTrial` itself.
  function onCreated (trial, { stay } = {}) {
    updateTrial(trial._id, trial) // Ensure trial added to the store
    api.users.me(u => updateUser(u._id, u)) // Refresh user to get new trial permissions
    api.users.createEmailSubscription(user._id, `trial.${trial._id}.participantMessages`, ({ subscriptions }) => updateUser(user._id, { subscriptions }))
    if (!stay) goToTrial(trial)
  }

  function goToTrial (trial) {
    navigate(`/trials/${trial._id}/manage`)
  }

  return (
    <Container>
      <Stack direction={{ base: 'column', md: 'row' }} gap={10}>
        <Box>
          <EmptyCard icon='research' title='Trials' description='A trial (or "project") is a space for defining your study and collecting data. Once created you can add other staff to help manage or collect your data, define your variables, setup your forms, and manage participants.' />
        </Box>
        <Stack flex={1} gap={5}>
          <Box>
            <Heading as='h2' size='lg'>Create a new trial</Heading>
            <Text>This trial will be managed by the {team && team.name} team.</Text>
          </Box>
          {/* Wait for the team: whether it has the trial builder decides
              which tabs there are. */}
          {team &&
            <SimpleTabs
              wrapped
              lazyMount
              tabs={[
                { key: 'scratch', title: 'Start from scratch', content: <FromScratch onCreated={onCreated} /> },
                hasBuilder && { key: 'builder', title: 'Trial builder', content: <TrialBuilder onCreated={onCreated} goToTrial={goToTrial} /> },
                { key: 'demo', title: 'Create a demo', content: <Demo onCreated={onCreated} /> }
              ]}
            />}
        </Stack>
      </Stack>
    </Container>
  )
}
